import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, ExternalLink, Phone, Truck } from 'lucide-react';
import { PageLayout } from '../components/global/PageLayout';
import { Section } from '../components/ui/PageIntro';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { ProductCard } from '../components/LowOnFuel/ProductCard';
import { ProductPrice, ProductStatusBadge, StockStatus } from '../components/LowOnFuel/ProductBits';
import { ReserveForm } from '../components/LowOnFuel/ReserveForm';
import { getProduct, getShopProducts } from '../data/products';
import { CONTACT, FORM_RESPONSE_TIME } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { NotFound } from './NotFound';
export function ProductDetail() {
  const {
    slug
  } = useParams();
  const product = slug ? getProduct(slug) : undefined;
  if (!product) return <NotFound data-id="element-715" data-mp="flan31bybdqu" />;
  return <ProductDetailView key={product.slug} slug={product.slug} data-id="element-716" data-mp="fkrhmk3hmhtp" />;
}

/**
 * Split out so the metadata hook is never called conditionally, and so
 * moving between two items remounts rather than carrying the previous
 * item's enquiry form state across.
 */
function ProductDetailView({
  slug
}: {
  slug: string;
}) {
  const product = getProduct(slug)!;
  useDocumentMeta({
    title: `${product.name} | Low On Fuel shop`,
    description: product.shortDescription,
    path: `/shop/${product.slug}`,
    ogImage: product.image,
    ogImageAlt: product.imageAlt
  });
  const related = getShopProducts().filter(candidate => candidate.id !== product.id).slice(0, 4);
  return <PageLayout data-id="element-717" data-mp="3tlamj0euhy4r">
      <div className="border-b border-hairline bg-sand" data-id="element-718" data-mp="3tlu58jcbkqju">
        <div className="mx-auto max-w-container px-gutter-sm py-6 sm:px-gutter-md lg:px-gutter-lg" data-id="element-719" data-mp="2fj4ypf4vttk">
          <Breadcrumbs items={[{
          label: 'Home',
          to: '/'
        }, {
          label: 'Shop',
          to: '/shop'
        }, {
          label: product.name
        }]} data-id="element-720" data-mp="2i6nh8wd44jun" />
        </div>
      </div>

      <Section className="pb-8 sm:pb-10" data-id="element-721" data-mp="232vm6yex05bd">
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14" data-id="element-722" data-mp="235zwmdiz3xah">
          {/* Photographs. The supporting shot shows the piece in the room it
              came out of, which is most of what tells you what it is. */}
          <div data-id="element-723" data-mp="1pda9u6vmeocb">
            <div className="overflow-hidden rounded-container border border-hairline bg-sand" data-id="element-724" data-mp="1pbpfoid7x7ez">
              <img src={product.image} alt={product.imageAlt} loading="eager" decoding="sync" {...{
              fetchpriority: 'high'
            }} className="aspect-square w-full object-cover" data-id="element-725" data-mp="1kdxlfhp3p97m" />
            </div>

            {/* The shared merchandise photograph is genuinely this item for
                the tees, caps and snoods. For everything else it is a stand
                in, and saying so is cheaper than an apology later. */}
            {product.photoPending && <p className="mt-3 flex items-start gap-2.5 rounded-container border border-hairline bg-white p-3 text-sm leading-relaxed text-ink/70" data-id="element-726" data-mp="38bga7fjvfmbd">
                <Camera className="mt-0.5 h-4 w-4 shrink-0 text-rust" aria-hidden="true" data-id="element-727" data-mp="3d98b2z2huirp" />
                <span data-id="element-728" data-mp="3n0cr823wo9o4">
                  We have not got round to photographing this one yet, so the
                  picture above is our merchandise rail. Give us a ring and we
                  will happily describe it, or send you a photo.
                </span>
              </p>}

            {product.supportingImages && product.supportingImages.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-4" data-id="element-729" data-mp="33cdfanxluwh4">
                  {product.supportingImages.map(supporting => <li key={supporting.src} data-id="element-730" data-mp="elau7zpwd5gz">
                      <img src={supporting.src} alt={supporting.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-container border border-hairline object-cover" data-id="element-731" data-mp="emlzy52wknaq" />
                    </li>)}
                </ul>}
          </div>

          <div data-id="element-732" data-mp="m421ifp27vq7">
            {product.labels.length > 0 && <ul className="flex flex-wrap gap-1.5" data-id="element-733" data-mp="m2qwkcjvohxi">
                {product.labels.map(label => <li key={label} data-id="element-734" data-mp="1020a14gp8xxs">
                    <ProductStatusBadge label={label} data-id="element-735" data-mp="v504qvrobz7q" />
                  </li>)}
              </ul>}

            <h1 className="mt-4 font-bebas text-5xl leading-[0.9] tracking-wide text-ink sm:text-6xl" data-id="element-736" data-mp="19sla6wc0uait">
              {product.name}
            </h1>

            {product.makerName && <p className="mt-2 text-ink/60" data-id="element-737" data-mp="1itjg5k60ss41">
                by{' '}
                {product.makerLink && product.makerLink !== '#' ? <a href={product.makerLink} className="inline-flex items-center gap-1 font-bold text-rust underline decoration-rust/40 underline-offset-2 hover:decoration-rust focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-738" data-mp="1eukcs3zxt3ep">
                    {product.makerName}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" data-id="element-739" data-mp="1tewtxrkir47i" />
                  </a> : <span className="font-bold text-ink" data-id="element-740" data-mp="1eph4cewq54te">
                    {product.makerName}
                  </span>}
              </p>}

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-741" data-mp="15q3sxgp1i661">
              <ProductPrice price={product.price} className="text-4xl" data-id="element-742" data-mp="15o0ymkgutqvi" />
              <StockStatus product={product} data-id="element-743" data-mp="19rp5f0xnwifp" />
            </div>

            <p className="mt-5 max-w-prose text-lg leading-relaxed text-ink/85" data-id="element-744" data-mp="vreexz09l5v2">
              {product.shortDescription}
            </p>

            {product.history && <div className="mt-6 rounded-container border border-hairline bg-white p-4" data-id="element-745" data-mp="32fohrbpyvxjj">
                <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-ink/60" data-id="element-746" data-mp="2xp7zneb7bnqu">
                  Item history
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-ink/75" data-id="element-747" data-mp="2toee2rl8el14">
                  {product.history}
                </p>
              </div>}

            {product.sold ? <div className="mt-7 rounded-container bg-ink p-6" data-id="element-748" data-mp="3ldrbg3tgmino">
                <h2 className="font-bebas text-3xl leading-none tracking-wide text-cream" data-id="element-749" data-mp="3hdguxx81k9uk">
                  This one's sold
                </h2>
                <p className="mt-2 leading-relaxed text-cream/80" data-id="element-750" data-mp="3ccjcdjj4ddsj">
                  It has found itself a new garage. Do have a look at what else
                  is on the walls, as there is usually something new up there.
                </p>
                <Link to="/shop" className="mt-5 inline-flex items-center justify-center rounded-container bg-flame px-6 py-3.5 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink" data-id="element-751" data-mp="1vzumur885u5d">
                  Visit the shop
                </Link>
              </div> : <p className="mt-7 flex items-start gap-2.5 text-sm leading-relaxed text-ink/70" data-id="element-752" data-mp="20mn40s0z90oq">
                <Truck className="mt-0.5 h-5 w-5 shrink-0 text-moss" aria-hidden="true" data-id="element-753" data-mp="24orrf90u112x" />
                {product.collectionNote ?? 'Once we have confirmed your reservation, we will give you a ring to sort out collection or delivery, whichever suits you better.'}
              </p>}

          </div>
        </div>

        {!product.sold && <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14" data-id="element-754" data-mp="p555q8260t9l">
            <div className="lg:order-2" data-id="element-755" data-mp="2r379jwztjcp0">
              <ReserveForm product={product} data-id="element-756" data-mp="2r25n6wdcr3rz" />
            </div>

            <div className="lg:order-1" data-id="element-757" data-mp="2m4duw18spfn9">
              <h2 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-758" data-mp="2m3b3ztbh3d7c">
                How buying works round here
              </h2>
              <ol className="mt-5 space-y-5" data-id="element-759" data-mp="2vtxcz4oop5h2">
                {[{
              title: 'You drop us a line',
              detail: 'Tell us which piece you have got your eye on and when you might collect. Nothing is charged and nothing is held just yet.'
            }, {
              title: 'We check it is still here',
              detail: `We will email you back ${FORM_RESPONSE_TIME}. A lot of the shop is one-off, so this is the step that really matters.`
            }, {
              title: 'We sort out paying and collecting',
              detail: 'Pay in the café when you come for it, or we can take payment over the telephone and post it out to you at cost.'
            }].map((step, index) => <li key={step.title} className="flex items-start gap-4" data-id="element-760" data-mp="4tg9jtdczze4" data-mpi={index}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rust text-sm font-black text-white" aria-hidden="true" data-id="element-761" data-mp="3m5r1lcjyvarg">
                      {index + 1}
                    </span>
                    <span data-id="element-762" data-mp="nkzck07dvdg5">
                      <span className="block font-bold text-ink" data-id="element-763" data-mp="nodb0xcxf2ib">
                        {step.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-ink/70" data-id="element-764" data-mp="dw7k7qyeur4p">
                        {step.detail}
                      </span>
                    </span>
                  </li>)}
              </ol>

              <a href={CONTACT.telephoneHref} className="mt-7 inline-flex items-center gap-2 rounded-container border border-control px-5 py-3 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-765" data-mp="16djysyskukaz">
                <Phone className="h-4 w-4 text-rust" aria-hidden="true" data-id="element-766" data-mp="1ivzf5wx20ber" />
                Ring us on {CONTACT.telephone}
              </a>
            </div>
          </div>}

        <div className="mt-12 border-t border-hairline pt-8" data-id="element-767" data-mp="144yzb6tz9f8l">
          <Link to="/shop" className="inline-flex items-center gap-2 rounded-container border border-control px-5 py-3 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2" data-id="element-768" data-mp="18ytmwlcpeecy">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-769" data-mp="l8r1up77i8nu" />
            Visit the shop
          </Link>
        </div>
      </Section>

      {related.length > 0 && <div className="border-t border-hairline bg-cream" data-id="element-770" data-mp="6kqofupsp7cm">
          <Section labelledBy="related-items-heading" data-id="element-771" data-mp="6nbrgn1txtby">
            <h2 id="related-items-heading" className="font-bebas text-4xl leading-none tracking-wide text-ink" data-id="element-772" data-mp="2l8f7w7c6kpm">
              More from the walls
            </h2>
            <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-773" data-mp="bjtwlo12ntot">
              {related.map(candidate => <li key={candidate.id} className="flex" data-id="element-774" data-mp="pijomp91b36b">
                  <div className="flex-1" data-id="element-775" data-mp="ljkmtdilnmj3">
                    <ProductCard product={candidate} data-id="element-776" data-mp="lgoss09vhhzx" />
                  </div>
                </li>)}
            </ul>
          </Section>
        </div>}
    </PageLayout>;
}